import { Component, OnInit, Inject, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from '../../custom-validators';
import { FormValidatorService } from '../../services/form-validator.service';
import { Validators } from '../../validators';
import { ChangePasswordService } from './change-password.service';
import {
  MsgProvider,
  TwoFactorAuthOtpService,
} from '../../services/two-factor-auth/two.factor.auth.service';
import Swal from 'sweetalert2';
import { interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit {
  oldPasswordHide: boolean = true;
  newPasswordHide: boolean = true;
  confirmNewPasswordHide: boolean = true;
  otpHide: boolean = true;
  formValidator!: FormValidatorService;
  changePasswordForm!: FormGroup;
  showResendOtp: boolean = false;
  @Input() redirectTo!: string;
  otpRateLimit: { timeS: number; countDown: number } = {
    timeS: 30,
    countDown: 30,
  };

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordComponent>,
    private translate: TranslateService,
    private changePassword: ChangePasswordService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private twoFactorAuthOtpService: TwoFactorAuthOtpService
  ) {}

  async ngOnInit() {
    this.formValidator = new FormValidatorService();
    this.changePasswordForm = this.formValidator.createForm([
      {
        fieldName: 'old_password',
        defaultValue: '',
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(/^(.{6,}$|$)/g),
            message: '',
          },
        ]
      },
      {
        fieldName: 'new_password',
        defaultValue: '',
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(/^(.{8,}$|$)/g),
            message: '',
          },
          {
            validatorFn: CustomValidators.differentPassword('old_password'),
            message: '',
          },
          {
            validatorFn: CustomValidators.complexPassword('s'),
            message: '',
          },
        ],
      },
      {
        fieldName: 'confirm_new_password',
        defaultValue: '',
        validators: [
          {
            validatorFn: Validators.required,
            message: '',
          },
          {
            validatorFn: CustomValidators.customPattern(/^(.{8,}$|$)/g),
            message: '',
          },
          {
            validatorFn: CustomValidators.confirmPasswordWith('new_password'),
            message: '',
          },
        ],
      },
    ]);
    this.translateValidationMessages();
    this.translate.onLangChange.subscribe(() => {
      this.translateValidationMessages();
    });
  }

  translateValidationMessages() {
    this.translate.get('FormValidation').subscribe(() => {
      this.formValidator.validationMessages = {
        old_password: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'Old password',
            fieldAr: 'كلمة السر القديمة',
          }),
          customPattern: this.translate.instant('FormValidation.MinLength', {
            field: 'Old password',
            fieldAr: 'كلمة السر القديمة',
            value: 6,
          }),
          oldPasswordNotEmpty: this.translate.instant('FormValidation.Empty', {
            field: 'Old password',
            fieldAr: 'كلمة السر القديمة',
          }),
        },
        new_password: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'New password',
            fieldAr: 'كلمة السر الجديدة',
          }),
          customPattern: this.translate.instant('FormValidation.MinLength', {
            field: 'New password',
            fieldAr: 'كلمة السر الجديدة',
            value: 8,
          }),
          differentPassword: this.translate.instant(
            'FORMS.VALIDATION.DIFFERENT',
            {
              field: 'New password',
              fieldAr: 'كلمة السر الجديدة',
              theOtherField: 'Old password',
              theOtherFieldAr: 'كلمة السر القديمة',
            }
          ),
          complexPassword: this.translate.instant(
            'FormValidation.complexPassword',
            {
              field: 'New password',
              fieldAr: 'كلمة السر الجديدة',
            }
          ),
        },
        confirm_new_password: {
          required: this.translate.instant('FormValidation.Empty', {
            field: 'Confirm new password',
            fieldAr: 'تأكيد كلمة السر الجديدة',
          }),
          minLength: this.translate.instant('FormValidation.MinLength', {
            field: 'Confirm new password',
            fieldAr: 'تأكيد كلمة السر الجديدة',
            value: 8,
          }),
          confirmPasswordWith: this.translate.instant(
            'FormValidation.Different',
            {
              field: 'Confirm new password',
              fieldAr: 'تأكيد كلمة السر الجديدة',
              theOtherField: 'New password',
              theOtherFieldAr: 'كلمة السر الجديدة',
            }
          ),
        },
      };
    });
  }

  async submit(otp: number) {
    this.changePassword.setParams({ ...this.changePasswordForm.value, otp });
    try {
      await this.changePassword.make();
      this.changePasswordForm.reset();
      await this.toastSuccessMessage();
    } catch (err: any) {
      console.log('error ', err);
      this.handleOtpErrors(err);
    }
  }

  async handleOtpErrors(err: any) {
    const isOtpError = this.twoFactorAuthOtpService.handleErrors(err);
    if (isOtpError) return;

    if (err?.faultyRequest?.error?.name === 'PasswordHistoryException') {
      await Swal.fire({
        icon: 'error',
        text: this.translate.instant('Verification.PasswordMustBeNew'),
        confirmButtonText: this.translate.instant('Sellers.Products.Confirm'),
      });

      this.formValidator.addError(
        'new_password',
        'wrong',
        this.translate.instant('FORMS.VALIDATION.IVALID', {
          field: 'Password',
        })
      );

      this.changePasswordForm.controls['new_password'].setErrors({
        wrong: 'invalid',
      });
    } else {
      this.formValidator.addError(
        'old_password',
        'wrong',
        this.translate.instant('FORMS.VALIDATION.IVALID', {
          field: 'Password',
        })
      );
      this.changePasswordForm.controls['old_password'].setErrors({
        wrong: 'invalid',
      });
    }
    this.formValidator.ShowErrors();
  }

  async sendOtpVia(provider: MsgProvider) {
    await this.twoFactorAuthOtpService.sendOtp('changePassword', provider);
  }

  tempDisableShowResendOtp(): void {
    const timer = document.getElementById('timer');
    const otpViaWhatsappBtn = document.getElementById(
      'otpViaWhatsappBtn'
    ) as HTMLButtonElement;

    timer.style.display = 'block';
    timer.style.setProperty('color', '#666');
    otpViaWhatsappBtn.disabled = true;
    this.otpRateLimit.countDown = this.otpRateLimit.timeS;

    interval(1000)
      .pipe(takeWhile(() => this.otpRateLimit.countDown >= 0))
      .subscribe(() => {
        timer.innerText = String(this.otpRateLimit.countDown--);
        if (this.otpRateLimit.countDown == 0) {
          timer.style.display = 'none';
          timer.style.setProperty('color', '#25D366');
          otpViaWhatsappBtn.removeAttribute('disabled');
        }
      });
  }

  async showOtpStep() {
    try {
      const html = `
        <div>
            <input
              type="number"
              id="swal-input"
              class="swal2-input"
              placeholder="${this.translate.instant(
                'Sellers.Products.EnterOtp'
              )}"
            />
            <p id="timer" style="display:none;"></p>
            <button
            id="otpViaWhatsappBtn"
            style="
              color: #25D366;
              background-color: transparent;
              border: none;
            "
          >
            ${this.translate.instant(
              'Sellers.Products.ClickHereToSendViaWhatsapp'
            )}
          </button>
        </div>
      `;
      await this.twoFactorAuthOtpService.sendOtp('changePassword');
      await Swal.fire({
        title: this.translate.instant('Sellers.Products.EnterOtp'),
        html: html,
        showCancelButton: true,
        confirmButtonText: this.translate.instant('Sellers.Products.Change'),
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          const otp = (
            document.getElementById('swal-input') as HTMLInputElement
          ).value;
          if (otp) {
            return await this.submit(Number(otp));
          }
        },
        onOpen: () => {
          const whatsappButton = document.getElementById('otpViaWhatsappBtn');
          whatsappButton?.addEventListener('click', async () => {
            try {
              Swal.showLoading();
              this.tempDisableShowResendOtp();
              await this.sendOtpVia('WHATSAPP');
            } catch (err) {
              this.handleOtpErrors(err);
              Swal.close();
            } finally {
              Swal.hideLoading();
            }
          });
        },
        allowOutsideClick: () => !Swal.isLoading(),
      });
    } catch (error) {
      console.log({ error });
    }
  }

  private async toastSuccessMessage(): Promise<void> {
    const currentLanguage = localStorage.getItem('language');
    let title = 'Your Password has been changed successfully!';

    if (currentLanguage === 'ar') {
      title = 'لقد تم تغيير كلمة المرور الخاصة بك بنجاح!';
    }

    await Swal.fire({
      icon: 'success',
      title,
      showConfirmButton: false,
      timer: 1500,
    });
  }
}
